import {Observable} from "rxjs";
import {Injectable} from "@angular/core";
import {MobileNativeAudio} from "../cspa.api";
import {LogsService} from "../../../../utils/services/logs.service";
import {AndroidBridgeBase} from "../../../../mobile/api-services/bridge/android-bridge-base";

/**
 * Methods which may be called by native in web app
 */
interface AndroidWebBridge {
  /**
   * call after you provide native bridge implementation to the WebView
   */
  initialize(): void;
  /**
   * Call if data state has been changed and you
   * expect the current View data should be reloaded
   */
  notifyDataUpdate(): void;
}

/**
 * Methods which may be called by web in native
 */
interface NativeBridge {

  /**
   * store the data for the coming request. Request is identified by provided numeric value
   * which will be provided in "X-Request-Id" header
   * @param id of the request which data belongs to
   * @param data
   */
  storeRequestData(id: string, data: string): void;

  /**
   * clear the recording buffer
   * @param id request id
   */
  clear(id: number): void;

  /**
   * start sound recording
   * @param id request id
   */
  record(id: number): void;

  /**
   * stop sound recording or playing
   * @param id
   */
  stop(id: number): void;

  /**
   * play recorded sound (if is still recording stop recording first)
   * @param id
   */
  play(id: number): void;

  /**
   * terminate (release) the recording system (for example you may need to stop recording,
   * release a media player or media recorder
   * @param id
   */
  terminate(id: number): void;

  /**
   * Initialize sound system. I.e. ask for privileges
   * @param id
   * @return true if is initialized correctly and allows to record the sound
   */
  init(id: number): boolean;

  /**
   * close the web application. Remember check if sound recording device is terminated
   */
  close(id: number): void;

  /**
   * start bridge initialization
   */
  begin(): void;
}

/**
 * The service is responsible for the communication between native and web part.
 * Before a full communication channel would be established the method initialize() has to be invoked first
 * by the native.
 *
 * The communication uses two different techniques. Methods related to the sound recording device
 * calls native api directly. Other uses a REST protocol which has to be intercepted by a native.
 * For data manipulation requests like POST or PUT we had to prepare a special
 * protocol for data providing (because request body can't be read during the native intercept phase).
 * For that cases api calls storeRequestData() method which provides a request body
 * before a regular request call.
 *
 * The URL scheme is formatted in typical tree structure:
 * + /sets +                                              - set definitions container
 * |       + /${exerciseSet} +                            - specific set content definitions container (chapters)
 * |                         + availability               - specific set availabilities container
 * |                         + questions                  - specific set question definitions container
 * + sessions +                                           - device stored sessions container
 *            + current                                   - device current session content
 */
@Injectable({
  providedIn: 'root'
})
export class AndroidRequestBasedNativeApi implements MobileNativeAudio, AndroidWebBridge {


  constructor(
    private logger: LogsService,
    private bridgeBase: AndroidBridgeBase
    ) {
  }

  initialize() {
    this.bridgeBase.initialize();
  }

  notifyDataUpdate(): void {
    this.logger.log('notifying data update');
    this.bridgeBase.updateEventsSubject.next(null);
  }

  log(text: string) {
    this.logger.log(`[android-api2] - ${text}`);
  }

  clear(): Observable<void> {
    this.log("calling clear");
    return this.bridgeBase.callNative<void>(
      id => (window.nativeBridge as NativeBridge).clear(id)
    );
  }

  /**
   * start recording
   */
  record() {
    this.log("calling record");
    return this.bridgeBase.callNative<void>(
      id => (window.nativeBridge as NativeBridge).record(id)
    ).subscribe();
  }

  /**
   * stop recording or playback
   */
  stop(): Observable<void> {
    this.log("calling stop");
    return this.bridgeBase.callNative<void>(id => (window.nativeBridge as NativeBridge).stop(id));
  }

  /**
   * start recording playback
   */
  play2() {
    this.log("calling play");
    return this.bridgeBase.callNative<void>(
      id => (window.nativeBridge as NativeBridge).play(id)
    ).subscribe();
  }

  /**
   * clean up and release recording device
   */
  terminate() {
    this.log("calling terminate");
    return this.bridgeBase.callNative<void>(
      id => (window.nativeBridge as NativeBridge).terminate(id)
    ).subscribe();
  }

  /**
   * initialize recording device, returns true if positive
   * @param callback
   */
  initAudio(callback: (state: boolean) => void) {
    this.log(" calling clear");
    return this.bridgeBase.callNative<boolean>(
      id => (window.nativeBridge as NativeBridge).init(id)
    ).subscribe(
      it => callback(it)
    )
  }
}
